import { GridCellParams, GridEditInputCell } from "@mui/x-data-grid";
import { GridEditTextProps } from "./buyerListingTypes";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { styled, Theme } from "@mui/material/styles";
import { useState, useEffect, useCallback, memo } from "react";
import Fade from "@mui/material/Fade";

/** Tooltip styles*/
const getTooltipStyles = (theme: Theme) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  [`& .${tooltipClasses.arrow}`]: {
    "&::before": {
      backgroundColor: theme.palette.error.main,
    },
  },
});

/** Styled Tooltip to show validation errors */
export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    placement="top"
    arrow
    classes={{ popper: className }}
    slots={{
      transition: Fade,
    }}
    slotProps={{
      transition: { timeout: 300 },
      popper: {
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, -20],
            },
          },
        ],
      },
    }}
  />
))(({ theme }) => ({
  ...getTooltipStyles(theme),
}));

/** Input cell component for the grid cell edit */
const RenderInputCell = memo(
  ({
    params,
    hasError,
    handleBlurEvent,
    handleKeyDownEvent,
  }: {
    params: GridCellParams;
    hasError: boolean;
    handleBlurEvent: (event: React.SyntheticEvent<HTMLInputElement>) => void;
    handleKeyDownEvent: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  }) => {
    return (
      <GridEditInputCell
        debounceMs={200}
        onBlur={handleBlurEvent}
        onKeyDown={handleKeyDownEvent}
        variant="standard"
        {...{ ...params, error: hasError }}
      />
    );
  }
);

/** Functional component for editing text in the grid */
const BuyerListingGridEditText: React.FC<GridEditTextProps> = ({
  id,
  field,
  handleChangeText,
  params,
}) => {
  const [showError, setShowError] = useState(false);
  const error = (params as { error?: string }).error;
  const hasError = !!error;
  const ERROR_DISPLAY_DURATION = 5000;

  /** Clear the error tooltip from the view after a specified duration */
  useEffect(() => {
    if (showError) {
      const timer = setTimeout(
        () => setShowError(false),
        ERROR_DISPLAY_DURATION
      );
      return () => clearTimeout(timer);
    }
  }, [showError]);

  /** Handle the blur event */
  const handleBlurEvent = useCallback(
    async (event: React.SyntheticEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value.trim();
      if (!hasError) {
        handleChangeText(id.toString(), field, value, params);
      } else {
        setShowError(hasError);
      }
    },
    [hasError, id, field, handleChangeText, params]
  );

  /** Handle the Enter and Tab events */
  const handleKeyDownEvent = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === "Tab") {
        handleBlurEvent(event);
      }
    },
    [handleBlurEvent]
  );

  return (
    <StyledTooltip open={showError} title={error}>
      <div>
        <RenderInputCell
          params={params}
          hasError={hasError}
          handleBlurEvent={handleBlurEvent}
          handleKeyDownEvent={handleKeyDownEvent}
        />
      </div>
    </StyledTooltip>
  );
};

export default BuyerListingGridEditText;
